/** A catalog record's server catalog field as the records core reads it (plan §10.3): the
 *  activity, activity category and itinerary edit catalogs (`GET …/edit/catalog/`) name each
 *  field's input by WHAT it edits (`url`, `phone`, `atags`); the records core names its
 *  inputs by their SHAPE. `coreWidget` maps one to the other, so a catalog record's VIEW half
 *  (its registry) and EDIT half (the server catalog) hand FieldSlot the same widget — the
 *  same rendering in both modes:
 *
 *  - `url`, `phone` → `text`: a text input whose value actions (open, call, copy) ride in
 *    its end adornment, as the view shows them;
 *  - `atags`, `regions`, `trip_types` → `tags`: the core's id set — grouped chips (kept /
 *    added / removed), an "Add …" search, the ledger's Added / Removed lines (the itinerary's
 *    catalog search tags are `tags` already).
 *
 *  Every other name is the core's own (`text`, `enum`, `rich_text`, `duration`, …) or the
 *  record kind's picker, registered under the catalog's name (`category`, `country`,
 *  `currency`, `coordinates`, `activity_list`). */
import type { RecordField, RecordFieldKind } from '@/features/records/editor/types';

const CORE_WIDGET: Readonly<Record<string, string>> = {
  url: 'text',
  phone: 'text',
  atags: 'tags',
  regions: 'tags',
  trip_types: 'tags',
};

/** The records core's input for a catalog widget name (null: no input). */
export function coreWidget(widget: string | null | undefined): string | null {
  if (!widget) return null;
  return CORE_WIDGET[widget] ?? widget;
}

/** What the catalog records' catalog fields share (`ActivityRecordCatalogField` and its
 *  siblings). */
export interface CatalogRecordFieldLike {
  key: string;
  entity: string;
  label: string;
  kind: Extract<RecordFieldKind, 'editable' | 'vendor' | 'derived'>;
  widget: string | null;
  choices: ReadonlyArray<{ value: number | string; label: string }>;
  nullable: boolean;
  max_length?: number | null;
  permissions: readonly string[];
  guards: readonly string[];
  effects: readonly string[];
  help?: string;
  warning?: string;
}

/** A server catalog field as the records core's `RecordField`. */
export function catalogRecordField(f: CatalogRecordFieldLike): RecordField {
  return {
    key: f.key,
    entity: f.entity,
    label: f.label,
    kind: f.kind,
    widget: f.kind === 'editable' ? coreWidget(f.widget) : null,
    choices: f.choices.map((c) => ({ value: c.value, label: c.label })),
    nullable: f.nullable,
    max_length: f.max_length ?? null,
    guards: [...f.guards],
    permissions: [...f.permissions],
    follows_lead: false,
    effects: [...f.effects],
    automatic: [],
    restech_rule: null,
    ...(f.help ? { help: f.help } : {}),
    ...(f.warning ? { warning: f.warning } : {}),
  };
}
